import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, History as HistoryIcon, Loader2, MessageSquare, PenLine, Settings2, Users } from 'lucide-react';
import DwShell from '../components/home/dw/DwShell';
import { API, errMsg, money, render, segments, useBulkSms } from '../components/bulksms/api';
import { RecipientsStep } from '../components/bulksms/Recipients';
import { MessageStep } from '../components/bulksms/Message';
import { ReviewStep, ConfirmSend } from '../components/bulksms/Review';
import { History } from '../components/bulksms/History';
import { Settings } from '../components/bulksms/Settings';
import '../components/bulksms/bulksms.css';

const STEPS = [['recipients', 'Recipients', Users], ['message', 'Message', PenLine], ['review', 'Review & send', MessageSquare]];
const EMPTY = { categories: [], contacts: [], leads: [], manual: '' };

const PhonePreview = ({ text, name }) => (
  <div className="bsx-phone" data-testid="bsx-preview">
    <div className="bsx-phone-top"><span className="bsx-av">{(name || 'C')[0]}</span><b>{name || 'Client'}</b></div>
    <div className="bsx-phone-body">{text ? <p className="bsx-bubble" data-testid="bsx-preview-text">{text}</p> : <p className="bsx-muted">Your message preview appears here</p>}</div>
  </div>
);

export default function BulkSms() {
  const { headers, overview, loadOverview, templates, loadTemplates } = useBulkSms();
  const [tab, setTab] = useState('compose');
  const [step, setStep] = useState(0);
  const [aud, setAud] = useState(EMPTY);
  const [body, setBody] = useState('');
  const [marketing, setMarketing] = useState(true);
  const [resolved, setResolved] = useState(null);
  const [resolving, setResolving] = useState(false);
  const [removed, setRemoved] = useState(new Set());
  const [when, setWhen] = useState('now');
  const [at, setAt] = useState('');
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [histKey, setHistKey] = useState(0);
  const t = useRef();

  const audBody = useMemo(() => ({ categories: aud.categories, contact_ids: aud.contacts.map((c) => c.id), lead_ids: aud.leads.map((l) => l.id), manual: aud.manual }), [aud]);
  const hasAud = audBody.categories.length || audBody.contact_ids.length || audBody.lead_ids.length || aud.manual.trim();
  useEffect(() => {
    clearTimeout(t.current);
    if (!hasAud) { setResolved(null); return undefined; }
    setResolving(true);
    t.current = setTimeout(() => axios.post(`${API}/resolve`, audBody, headers).then(({ data }) => setResolved(data)).catch((e) => toast.error(errMsg(e))).finally(() => setResolving(false)), 350);
    return () => clearTimeout(t.current);
  }, [audBody, hasAud, headers]);
  useEffect(() => { setRemoved(new Set()); }, [audBody]);

  const recips = (resolved?.recipients || []).filter((r) => !removed.has(r.phone));
  const cost = overview?.settings?.cost_per_sms ?? 0.077;
  const parts = body ? recips.reduce((n, r) => n + segments(render(body, r, marketing)), 0) : 0;
  const sample = recips[0] || resolved?.recipients?.[0];
  const preview = body ? render(body, sample, marketing) : '';
  const canNext = step === 0 ? recips.length > 0 : step === 1 ? body.trim().length > 0 : recips.length > 0 && (when === 'now' || at);

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/campaigns`, { audience: { ...audBody, removed: [...removed] }, body, marketing, confirm: true, expected_count: recips.length, schedule_at_local: when === 'later' ? at : null }, headers);
      toast.success(when === 'later' ? `Scheduled for ${data.scheduled_label}` : `Sending to ${data.recipient_count} people`);
      setConfirm(false); setAud(EMPTY); setBody(''); setStep(0); setWhen('now'); setAt('');
      setTab('history'); setOpenId(data.id); setHistKey((k) => k + 1);
    } catch (e) { toast.error(errMsg(e, 'Could not send')); } finally { setBusy(false); }
  };

  return (
    <DwShell activeKey="bulk-sms">
      <div className="bsx" data-testid="bulk-sms-page">
        <header className="bsx-head">
          <div><span className="bsx-eyebrow">Communication</span><h1>Bulk SMS</h1><p>Text contacts and leads straight from DerwentAI. Lists update automatically as deals move through the pipeline.</p></div>
          <nav className="bsx-tabs" role="tablist">
            {[['compose', 'New message', PenLine], ['history', 'History', HistoryIcon], ['settings', 'Templates & opt-outs', Settings2]].map(([k, l, Ic]) => (
              <button type="button" key={k} role="tab" aria-selected={tab === k} className={tab === k ? 'on' : ''} onClick={() => setTab(k)} data-testid={`bsx-tab-${k}`}><Ic size={15} />{l}</button>
            ))}
          </nav>
        </header>

        {tab === 'compose' && (
          <div className="bsx-grid">
            <div className="bsx-main card">
              <ol className="bsx-steps">{STEPS.map(([k, l, Ic], i) => (
                <li key={k} className={i === step ? 'on' : i < step ? 'done' : ''}><button type="button" disabled={i > step && !(i === 1 && recips.length) && !(i === 2 && recips.length && body.trim())} onClick={() => setStep(i)} data-testid={`bsx-step-${k}`}><span className="dot"><Ic size={14} /></span>{l}</button></li>
              ))}</ol>
              {step === 0 && <RecipientsStep overview={overview} headers={headers} aud={aud} setAud={setAud} />}
              {step === 1 && <MessageStep body={body} setBody={setBody} marketing={marketing} setMarketing={setMarketing} templates={templates} headers={headers} onTemplatesChanged={loadTemplates} />}
              {step === 2 && <ReviewStep resolved={resolved} removed={removed} setRemoved={setRemoved} body={body} marketing={marketing} when={when} setWhen={setWhen} at={at} setAt={setAt} />}
              <footer className="bsx-foot">
                {step > 0 ? <button type="button" className="btn btn-ghost" onClick={() => setStep(step - 1)} data-testid="bsx-back"><ArrowLeft size={15} />Back</button> : <span />}
                {step < 2
                  ? <button type="button" className="btn btn-solid" disabled={!canNext} onClick={() => setStep(step + 1)} data-testid="bsx-next">Continue<ArrowRight size={15} /></button>
                  : <button type="button" className="btn btn-solid" disabled={!canNext} onClick={() => setConfirm(true)} data-testid="bsx-send">{when === 'later' ? 'Schedule' : 'Send'} to {recips.length}<ArrowRight size={15} /></button>}
              </footer>
            </div>
            <aside className="bsx-rail">
              <div className="card bsx-sum" data-testid="bsx-summary">
                <span className="bsx-eyebrow">Summary</span>
                <div className="bsx-sum-big"><b className="num" data-testid="bsx-recipient-count">{resolving ? <Loader2 size={22} className="animate-spin" /> : recips.length}</b><small>recipient{recips.length === 1 ? '' : 's'}</small></div>
                <dl>
                  <div><dt>Duplicates merged</dt><dd className="num">{resolved?.duplicates_removed || 0}</dd></div>
                  <div><dt>Excluded</dt><dd className="num">{resolved?.excluded?.length || 0}</dd></div>
                  <div><dt>SMS parts</dt><dd className="num">{parts || '–'}</dd></div>
                  <div className="em"><dt>Estimated cost</dt><dd className="num" data-testid="bsx-est-cost">{parts ? money(parts * cost) : '–'}</dd></div>
                </dl>
                <small className="bsx-muted">${Number(cost).toFixed(3)} per SMS part · excl. GST</small>
              </div>
              <PhonePreview text={preview} name={sample?.name} />
            </aside>
          </div>
        )}
        {tab === 'history' && <History headers={headers} openId={openId} setOpenId={setOpenId} refreshKey={histKey} />}
        {tab === 'settings' && <Settings headers={headers} overview={overview} templates={templates} onTemplates={loadTemplates} onOverview={loadOverview} />}
      </div>
      {confirm && <ConfirmSend count={recips.length} parts={parts} cost={cost} when={when} at={at} busy={busy} onClose={() => setConfirm(false)} onSend={send} />}
    </DwShell>
  );
}
